import { useState, useContext, useEffect } from 'react';
import { Typography, Container, useMediaQuery, Button, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ListItemButton from '@mui/material/ListItemButton';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import Popup from './Popup';
import AssetHeader from './AssetsHeader';
import { AppContext } from '../App';
import QuerioLogo from '../assets/QuerioLogo.svg';
import DiscordLogo from '../assets/DiscordLogo.svg';
import DiscordLogoBlack from '../assets/DiscordLogoBlack.svg';
import OpenChatLogo from '../assets/featured/openchat.png';
import TelegramLogo from '../assets/TelegramLogo.svg';

// Assuming these logos exist in your assets folder
import EthLogo from '../assets/ETHLogo.svg';
import IcpLogo from '../assets/ICPLogo.svg';

function Bridge() {
    const [showPopup, setShowPopup] = useState(false);
    const [popupType, setPopupType] = useState('');
    const [popupMessage, setPopupMessage] = useState('');

    const { mode, setMode } = useContext(AppContext);
    const matches = useMediaQuery('(min-width:600px)');
    const navigate = useNavigate();

    const showPopupWarning = (message) => {
        setShowPopup(true);
        setPopupType('warning');
        setPopupMessage(message);
    };

    // Use useEffect to trigger the popup once when `matches` is false
    useEffect(() => {
        if (!matches) {
            showPopupWarning('Access is available exclusively on laptops and desktops.');
        }
    }, [matches]);

    return (
        <>
            <AssetHeader />

            {showPopup && <Popup type={popupType} message={popupMessage} setShowPopup={setShowPopup} />}

            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: '100vh',
                    height: '100%',
                    boxShadow: 3,
                    overflow: 'auto',
                    paddingTop: '2rem'
                }}
            >
                <img
                    src={QuerioLogo}
                    alt="Querio Logo"
                    style={{ width: matches ? '150px' : '75px', marginTop: '2rem', marginBottom: '2rem', cursor: 'pointer' }}
                    onClick={() => navigate("/")}
                />
                <Typography
                    sx={{
                        fontSize: '32px',
                        fontWeight: 500,
                        lineHeight: '44px',
                        letterSpacing: '0rem',
                        textAlign: 'left',
                        marginBottom: '1.5rem'
                    }}
                >
                    Querio Bridge
                </Typography>
                <Typography
                    sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        lineHeight: '24px',
                        letterSpacing: '0rem',
                        textAlign: 'center',
                        marginBottom: '3.5rem',
                        width: '50%',
                    }}
                >
                    Easily convert between ICRC‑1 and ERC‑20 QRO tokens at a 1:1 rate.
                    <br />
                    <br />
                    <Box sx={{ justifyContent: 'center' }}>
                        <ListItemButton
                            href="https://oc.app/community/qbzct-jaaaa-aaaar-au2gq-cai/?ref=jviq4-waaaa-aaaar-aqq7a-cai"
                            sx={{
                                paddingX: 0,
                                display: 'inline-flex',
                                justifyContent: 'center',
                                width: '3rem'
                            }}
                        >
                            <img src={OpenChatLogo} alt="Open Chat" style={{ width: '1.65rem', height: '1.65rem' }} />
                        </ListItemButton>

                        <ListItemButton
                            href="https://discord.com/invite/Jvb8Xmzgdv"
                            sx={{
                                paddingX: 0,
                                display: 'inline-flex',
                                justifyContent: 'center',
                                width: '3rem'
                            }}
                        >
                            <img src={mode === 'light' ? DiscordLogoBlack : DiscordLogo} alt="Discord" style={{ width: '1.85rem', height: '1.65rem' }} />
                        </ListItemButton>
                        <ListItemButton
                            href="https://t.me/+VXpWvtRUSyU0MmFk"
                            sx={{
                                paddingX: 0,
                                display: 'inline-flex',
                                justifyContent: 'center',
                                width: '3rem'
                            }}
                        >
                            <img src={TelegramLogo} alt="Telegram" style={{ width: '1.85rem', height: '1.85rem' }} />
                        </ListItemButton>
                    </Box>
                </Typography>

                {/* New square buttons for conversion with directional logos and text */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginTop: '2rem' }}>
                                        {/* ERC-20 to ICRC-1: contained with solid hover color */}
                                        <Button
                        variant="contained"
                        href="/?bridge-to-icrc1=1"
                        sx={{
                            width: '140px',
                            height: '140px',
                            background: '#FBB03B',
                            boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
                            borderRadius: '8px',
                            padding: 0,
                            minWidth: '0',
                            textTransform: 'none',
                            transition: 'transform 0.3s ease, background-color 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#ff7b3e',
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box 
                                    component="img" 
                                    src={EthLogo} 
                                    alt="ETH Logo" 
                                    sx={{ width: '40px', height: '40px', mr: 0.5 }} 
                                />
                                <ArrowForwardIcon sx={{ fontSize: 24 }} />
                                <Box 
                                    component="img" 
                                    src={IcpLogo} 
                                    alt="ICP Logo" 
                                    sx={{ width: '40px', height: '40px', ml: 0.5 }} 
                                />
                            </Box>
                            <Typography variant="caption" sx={{ mt: 1, fontWeight: 'bold', textAlign: 'center' }}>
                                ERC-20 to ICRC-1
                            </Typography>
                        </Box>
                    </Button>
                    {/* ICRC-1 to ERC-20: outlined with border only */}
                    <Button
                        variant="outlined"
                        href="/?bridge-to-erc20=1"
                        sx={{
                            width: '140px',
                            height: '140px',
                            background: 'transparent',
                            border: '2px solid #FBB03B',
                            borderRadius: '8px',
                            padding: 0,
                            minWidth: '0',
                            textTransform: 'none',
                            color: '#FBB03B',
                            transition: 'transform 0.3s ease, background-color 0.3s ease, color 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#FBB03B',
                                color: '#ffffff'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box 
                                    component="img" 
                                    src={IcpLogo} 
                                    alt="ICP Logo" 
                                    sx={{ width: '40px', height: '40px', mr: 0.5 }} 
                                />
                                <ArrowForwardIcon sx={{ fontSize: 24 }} />
                                <Box 
                                    component="img" 
                                    src={EthLogo} 
                                    alt="ETH Logo" 
                                    sx={{ width: '40px', height: '40px', ml: 0.5 }} 
                                />
                            </Box>
                            <Typography variant="caption" sx={{ mt: 1, fontWeight: 'bold', textAlign: 'center' }}>
                                ICRC-1 to ERC-20
                            </Typography>
                        </Box>
                    </Button>
                </Box>
            </Container>
        </>
    );
}

export default Bridge;
